import {
  Appointment,
  AppointmentType,
  AttributionDetails,
} from '@generated/gql/graphql';
import { AssistantType } from '@src/types/attribution';
import { UserInformation } from '@src/types/auth';
import {
  AssistantTypeTracking,
  ReferrerTypeTracking,
  SalesMethodTracking,
  SessionTypeTracking,
} from '@src/types/tracking';
import { SignupApplicationEntity } from '@tracking/trackingEvents';
import { isNullish } from '@utils/isNullish';

const defaultAttributionContext = {
  referrerType: ReferrerTypeTracking.DIRECT,
  assistantType: AssistantTypeTracking.UNASSISTED,
  referrerDetails: undefined,
  assistantDetails: undefined,
  isSupported: false,
  supportingPartnerPositionId: undefined,
};

const getSessionTypeContext = (appointment: Nullable<Appointment>) => {
  if (appointment?.type === AppointmentType.PartnerRemote) {
    return SessionTypeTracking.Remote;
  }

  return SessionTypeTracking.FaceToFace;
};

const getSalesMethodContext = (
  appointment: Nullable<Appointment>,
  userInformation: Nullable<UserInformation>
) => {
  if (isNullish(appointment) && isNullish(userInformation)) {
    return SalesMethodTracking.Unassisted;
  }

  if (isNullish(appointment)) {
    if (isNullish(userInformation)) {
      return SalesMethodTracking.Unassisted;
    }

    const { type } = userInformation;

    switch (type) {
      case AssistantType.PARTNER:
        return SalesMethodTracking.PartnerAssistedRemote;
      case AssistantType.SALES:
        return SalesMethodTracking.SalesAssisted;
      default:
        return SalesMethodTracking.Unassisted;
    }
  }

  const { type } = appointment;

  switch (type) {
    case AppointmentType.FaceToFaceWithRemoteSupport:
    case AppointmentType.FaceToFace:
      return SalesMethodTracking.PartnerAssistedInPerson;
    case AppointmentType.PartnerRemote:
      return SalesMethodTracking.PartnerAssistedRemote;
    default:
      return SalesMethodTracking.Unassisted;
  }
};

const getPartnerContext = (userInformation: Nullable<UserInformation>) => {
  if (
    isNullish(userInformation) ||
    userInformation.type !== AssistantType.PARTNER
  ) {
    return {
      partnerId: undefined,
      partnerPositionId: undefined,
    };
  }

  return {
    partnerId: userInformation.partnerId,
    partnerPositionId: userInformation.partnerPositionId,
  };
};

type AttributionContext = Pick<
  SignupApplicationEntity,
  | 'referrer_type'
  | 'referrer_details'
  | 'assistant_type'
  | 'assistant_details'
  | 'is_supported'
  | 'supporting_partner_position_id'
>;

const parseAttributionContext = (
  attributionDetails: Nullable<AttributionDetails>
): AttributionContext => {
  if (isNullish(attributionDetails)) {
    return {
      referrer_type: defaultAttributionContext.referrerType,
      referrer_details: defaultAttributionContext.referrerDetails,
      assistant_type: defaultAttributionContext.assistantType,
      assistant_details: defaultAttributionContext.assistantDetails,
      is_supported: defaultAttributionContext.isSupported,
      supporting_partner_position_id:
        defaultAttributionContext.supportingPartnerPositionId,
    };
  }

  const { supportingPartner, referrer, assistant } = attributionDetails;

  let referrerType = ReferrerTypeTracking.DIRECT;
  let referrerDetails = undefined;
  let assistantType = AssistantTypeTracking.UNASSISTED;
  let assistantDetails = undefined;

  if (referrer) {
    if (referrer.affiliate) {
      referrerType = ReferrerTypeTracking.AFFILIATE;
      referrerDetails = 'uswitch';
    } else if (referrer.connector) {
      referrerType = ReferrerTypeTracking.CONNECTOR;
      referrerDetails = referrer.connector.shortId;
    } else if (referrer.customer || referrer.customerManual) {
      referrerType = ReferrerTypeTracking.CUSTOMER;
      referrerDetails =
        referrer.customer?.accountNumber ||
        referrer.customerManual?.accountNumber ||
        'customer';
    } else if (referrer.partner || referrer.partnerManual) {
      referrerType = ReferrerTypeTracking.PARTNER;
      const partnerRef = referrer.partner || referrer.partnerManual;
      referrerDetails = partnerRef?.positionId || 'partner';
    } else if (referrer.sales) {
      referrerType = ReferrerTypeTracking.SALES;
      referrerDetails = referrer.sales.oktaUsername;
    }
  }

  if (assistant) {
    if (assistant.partner) {
      assistantType = AssistantTypeTracking.PARTNER;
      assistantDetails = assistant.partner.positionId;
    } else if (assistant.sales) {
      assistantType = AssistantTypeTracking.SALES;
      assistantDetails = assistant.sales.oktaUsername;
    }
  }

  const isSupported = !isNullish(supportingPartner?.partner);
  const supportingPositionId = supportingPartner?.partner?.positionId;

  return {
    referrer_type: referrerType,
    referrer_details: referrerDetails,
    assistant_type: assistantType,
    assistant_details: assistantDetails,
    is_supported: isSupported,
    supporting_partner_position_id: supportingPositionId,
  };
};

const parseSessionContext = (
  appointment: Nullable<Appointment>,
  userInformation: Nullable<UserInformation>
): Pick<
  SignupApplicationEntity,
  'sales_method' | 'partner_id' | 'partner_position_id' | 'session_type'
> => {
  const { partnerId, partnerPositionId } = getPartnerContext(userInformation);

  return {
    sales_method: getSalesMethodContext(appointment, userInformation),
    session_type: getSessionTypeContext(appointment),
    partner_id: partnerId,
    partner_position_id: partnerPositionId,
  };
};

export { parseSessionContext, parseAttributionContext };
